import { useEffect, type ReactNode } from "react";

interface MasterDetailLayoutProps {
  /** The list view, rendered at full width below the slide-over. */
  list: ReactNode;
  /** Detail panel content. Render `null` to keep the panel closed. */
  detail: ReactNode | null;
  /**
   * Called when the user dismisses the panel (close button or Escape). The
   * caller is expected to clear whatever state drives `detail`.
   */
  onClose: () => void;
  /**
   * Optional aria-label for the slide-over region. Defaults to "Detail panel".
   */
  ariaLabel?: string;
}

/**
 * List + slide-over detail layout used by Devices, Rules, Schedules and other
 * master-detail surfaces. The list remains visible (and interactive) while the
 * panel covers the right portion of the viewport. ESC closes the panel.
 */
export function MasterDetailLayout({
  list,
  detail,
  onClose,
  ariaLabel,
}: MasterDetailLayoutProps) {
  const open = detail !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div className="master-detail">
      <div className="master-detail__list">{list}</div>
      <aside
        className={`master-detail__panel${open ? " master-detail__panel--open" : ""}`}
        role="dialog"
        aria-label={ariaLabel ?? "Detail panel"}
        aria-hidden={!open}
      >
        {detail}
      </aside>
    </div>
  );
}
